import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import SimpleLayout from '../components/SimpleLayout';
import Container from '../components/Container';
import Button from '../components/Button';
import Footer from '../components/Footer';

// Import screenshot images
import loginScreen from '../assets/camerascreenshots/login.png';
import mainScreen from '../assets/camerascreenshots/mainscreen.png';
import onlineTest from '../assets/camerascreenshots/onlinetest.png';

// Add enhanced styled components
const StatsBar = styled.div`
  display: flex;
  justify-content: center;
  gap: 3rem;
  margin: 2rem 0;
  padding: 1.5rem;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%);
  border-radius: 16px;
  border: 1px solid rgba(16, 185, 129, 0.2);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
    padding: 1.2rem;
  }
`;

const StatItem = styled.div`
  text-align: center;
  color: #fff;
`;

const StatNumber = styled.div`
  font-size: 1.8rem;
  font-weight: 900;
  color: #10b981;
  text-shadow: 0 0 10px rgba(16, 185, 129, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #b6d0ff;
  margin-top: 0.3rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const TestimonialSection = styled.div`
  margin: 3rem 0 2rem 0;
  text-align: center;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 2rem;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
    margin: 2rem auto 1.5rem auto;
  }
`;

const TestimonialTitle = styled.h3`
  font-size: 1.8rem;
  color: #fff;
  margin-bottom: 2rem;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
`;

const TestimonialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const TestimonialCard = styled.div`
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%);
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid rgba(110, 168, 255, 0.15);
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const TestimonialText = styled.p`
  font-size: 0.95rem;
  color: #e1e9ff;
  line-height: 1.5;
  font-style: italic;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const TestimonialAuthor = styled.div`
  color: #6ea8ff;
  font-weight: 600;
  font-size: 0.85rem;
`;

const TrustSignals = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  margin: 2rem auto;
  padding: 1.5rem;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
  border-radius: 16px;
  border: 1px solid rgba(110, 168, 255, 0.1);
  max-width: 1000px;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 1rem;
    padding: 1.2rem;
  }
`;

const TrustItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #b6d0ff;
  font-size: 0.9rem;
  
  &:before {
    content: '✓';
    color: #10b981;
    font-weight: bold;
  }
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const UrgencyBanner = styled.div`
  background: linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%);
  color: white;
  text-align: center;
  padding: 0.8rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    padding: 0.7rem;
    font-size: 0.95rem;
    margin-bottom: 1.5rem;
  }
`;

const DetailedFeaturesSection = styled.div`
  margin: 3rem 0;
  padding: 0 2rem;
  
  @media (max-width: 768px) {
    margin: 2rem 0;
    padding: 0 1rem;
  }
`;

const DetailedFeaturesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  background: 
    linear-gradient(145deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%),
    linear-gradient(145deg, rgba(30, 42, 74, 0.8) 0%, rgba(20, 30, 60, 0.9) 100%);
  border-radius: 24px;
  padding: 3rem 2rem;
  border: 1px solid rgba(110, 168, 255, 0.2);
  backdrop-filter: blur(20px);
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  text-align: center;
  color: #fff;
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const DetailedFeaturesTitle = styled.h2`
  font-size: 2.2rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  color: #fff;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const DetailedFeaturesDescription = styled.p`
  color: #b6d0ff;
  font-size: 1.15rem;
  margin: 0 auto 2rem auto;
  max-width: 700px;
  line-height: 1.7;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const FeatureContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const FeatureCard = styled.div`
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%);
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid rgba(110, 168, 255, 0.15);
  backdrop-filter: blur(10px);
  text-align: center;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const FeatureIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 1rem;
`;

const FeatureTitle = styled.h3`
  color: #6ea8ff;
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 0.8rem;
`;

const FeatureDescription = styled.p`
  color: #b6d0ff;
  font-size: 0.9rem;
  line-height: 1.5;
  opacity: 0.9;
`;

const PricingInfo = styled.div`
  background: 
    linear-gradient(145deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%),
    linear-gradient(145deg, rgba(30, 42, 74, 0.8) 0%, rgba(20, 30, 60, 0.9) 100%);
  border: 2px solid rgba(16, 185, 129, 0.4);
  border-radius: 24px;
  padding: 2.5rem 2rem;
  margin: 3rem auto;
  max-width: 500px;
  backdrop-filter: blur(20px);
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.3),
    0 0 20px rgba(16, 185, 129, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #10b981 0%, #059669 100%);
    border-radius: 24px 24px 0 0;
  }
  
  @media (max-width: 768px) {
    margin: 2rem 1rem;
    padding: 2rem 1.5rem;
  }
`;

const PricingTitle = styled.h3`
  color: #fff;
  font-size: 2.2rem;
  font-weight: 800;
  margin-bottom: 1rem;
  text-shadow: 0 2px 10px rgba(16, 185, 129, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const PricingDetails = styled.div`
  color: #10b981;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const PricingFeature = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 20px;
  font-size: 0.95rem;
  white-space: nowrap;
  
  &::before {
    content: '✓';
    color: #10b981;
    font-weight: bold;
  }
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

// Animation keyframes
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled components
const PageBackground = styled.div`
  min-height: 100vh;
  width: 100%;
  background: 
    radial-gradient(circle at 20% 50%, rgba(110, 168, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(45, 108, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 80%, rgba(58, 143, 255, 0.08) 0%, transparent 50%),
    linear-gradient(135deg, #0a0f2e 0%, #1a2451 50%, #0e1633 100%);
  position: relative;
  overflow: hidden;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.03;
  background-image: 
    linear-gradient(45deg, #6ea8ff 25%, transparent 25%),
    linear-gradient(-45deg, #6ea8ff 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #6ea8ff 75%),
    linear-gradient(-45deg, transparent 75%, #6ea8ff 75%);
  background-size: 60px 60px;
  background-position: 0 0, 0 30px, 30px -30px, -30px 0px;
  pointer-events: none;
`;

const HeaderSection = styled.div`
  width: 100%;
  padding: 4rem 2rem 2rem 2rem;
  position: relative;
  z-index: 2;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 2rem 1rem 1rem 1rem;
  }
`;

const BackToGateway = styled(Link)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  color: #b6d0ff;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0.75rem 1.5rem;
  background: rgba(110, 168, 255, 0.1);
  border-radius: 8px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: rgba(110, 168, 255, 0.2);
    color: #fff;
    transform: translateX(-2px);
  }
  
  @media (max-width: 768px) {
    top: 1rem;
    left: 1rem;
    font-size: 0.8rem;
    padding: 0.6rem 1rem;
  }
`;

const PaymentHeader = styled.div`
  width: 100%;
  padding: 1.5rem 2rem;
  background: rgba(10, 15, 46, 0.95);
  border-bottom: 1px solid rgba(110, 168, 255, 0.15);
  position: sticky;
  top: 0;
  z-index: 1000;
  backdrop-filter: blur(20px);
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const PaymentHeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
`;

const PaymentTitle = styled.h2`
  color: #fff;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const PaymentNavLinks = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const PaymentNavLink = styled.button`
  color: #b6d0ff;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0.5rem 1rem;
  background: ${props => props.variant === 'primary' ? 'rgba(110, 168, 255, 0.15)' : 'transparent'};
  border: 1px solid ${props => props.variant === 'primary' ? 'rgba(110, 168, 255, 0.3)' : 'rgba(110, 168, 255, 0.1)'};
  border-radius: 6px;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    background: rgba(110, 168, 255, 0.2);
    color: #fff;
  }
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 0.4rem 0.8rem;
  }
`;

const PageContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const PaymentContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-top: 3rem;
  
  @media (max-width: 768px) {
    padding-top: 2rem;
  }
`;

const PaymentContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const HeaderContainer = styled.div`
  max-width: 800px;
  margin: 0 auto 3rem auto;
`;

const HeaderTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 900;
  color: transparent;
  background: linear-gradient(135deg, #6ea8ff 0%, #ffffff 50%, #3a8fff 100%);
  background-clip: text;
  -webkit-background-clip: text;
  margin-bottom: 1rem;
  letter-spacing: -0.02em;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeaderDescription = styled.p`
  font-size: 1.2rem;
  color: #b6d0ff;
  margin-bottom: 2rem;
  line-height: 1.6;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const HeaderButton = styled.button`
  display: inline-block;
  padding: 1.2rem 3rem;
  font-size: 1.2rem;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(145deg, rgba(110, 168, 255, 0.3) 0%, rgba(58, 143, 255, 0.4) 100%);
  border: 1px solid rgba(110, 168, 255, 0.3);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  backdrop-filter: blur(20px);
  
  &:hover {
    background: linear-gradient(145deg, rgba(110, 168, 255, 0.4) 0%, rgba(58, 143, 255, 0.5) 100%);
    transform: translateY(-2px) scale(1.02);
    border-color: rgba(110, 168, 255, 0.5);
    box-shadow: 0 8px 24px rgba(110, 168, 255, 0.3);
    color: #fff;
  }
  
  @media (max-width: 768px) {
    padding: 1rem 2rem;
    font-size: 1.1rem;
    width: 100%;
    max-width: 300px;
  }
`;

const ConfirmationText = styled.p`
  font-size: 0.9rem;
  color: #6ea8ff;
  margin-top: 1rem;
  opacity: 0.8;
  font-weight: 500;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
    margin-top: 0.8rem;
  }
`;

const CarouselSection = styled.div`
  width: 100%;
  padding: 1rem 2rem 2rem 2rem;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    padding: 0.5rem 1rem 1rem 1rem;
  }
`;

const CarouselTitle = styled.h2`
  text-align: center;
  font-size: 1.8rem;
  color: #fff;
  margin-bottom: 2rem;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }
`;

const CarouselContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  background: transparent;
  border-radius: 24px;
  overflow: hidden;
`;

const CarouselImage = styled.img`
  width: 100%;
  height: 800px;
  object-fit: contain;
  position: ${props => props.index === 0 ? 'relative' : 'absolute'};
  top: ${props => props.index === 0 ? 'auto' : '0'};
  left: ${props => props.index === 0 ? 'auto' : '0'};
  opacity: ${props => props.$active ? '1' : '0'};
  transition: opacity 0.5s ease-in-out;
  border-radius: 24px;
  background: rgba(20, 30, 60, 0.3);
  
  @media (max-width: 768px) {
    height: 500px;
  }
`;

const CarouselControls = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
`;

const CarouselDot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: ${props => props.$active ? '#6ea8ff' : 'rgba(255, 255, 255, 0.3)'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #6ea8ff;
    transform: scale(1.2);
  }
`;

// Crypto selection components (kept for payment functionality)
const CryptoOptions = styled.div`
  margin-top: 2rem;
  margin-bottom: 2rem;
  background: rgba(20, 30, 60, 0.65);
  border-radius: 18px;
  padding: 1.5rem;
  box-shadow: 0 8px 32px 0 rgba(34, 60, 120, 0.18);
  border: 1.5px solid #2d6cff44;
  backdrop-filter: blur(8px);
  @media (max-width: 768px) {
    margin-top: 1.5rem;
    margin-bottom: 1.5rem;
    padding: 1.2rem;
  }
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const CryptoOptionsTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  text-align: center;
  color: #fff;
  text-shadow: 0 0 8px #2d6cffcc;
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 0.8rem;
  }
`;

const CryptoSelector = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.8rem;
    margin-top: 0.8rem;
  }
`;

const CryptoOption = styled.div`
  padding: 0.75rem;
  border: 2px solid ${props => props.selected ? '#2d6cff' : '#2d6cff44'};
  border-radius: 8px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.selected ? 'rgba(44, 108, 255, 0.12)' : 'rgba(20, 30, 60, 0.65)'};
  color: #b6d0ff;
  box-shadow: ${props => props.selected ? '0 0 12px #2d6cff88' : 'none'};
  &:hover {
    border-color: #2d6cff;
    transform: translateY(-2px);
    box-shadow: 0 0 16px #2d6cff88;
  }
  @media (max-width: 480px) {
    padding: 0.6rem;
  }
`;

const CryptoIcon = styled.div`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: #2d6cff;
  text-shadow: 0 0 8px #2d6cffcc;
  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 0.3rem;
  }
`;

const CryptoName = styled.div`
  font-size: 0.8rem;
  font-weight: ${props => props.selected ? 'bold' : 'normal'};
  color: #b6d0ff;
  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const PaymentSummary = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
  background: rgba(20, 30, 60, 0.65);
  border-radius: 18px;
  border: 1.5px solid #2d6cff44;
  box-shadow: 0 8px 32px 0 rgba(34, 60, 120, 0.18);
  color: #fff;
  @media (max-width: 768px) {
    margin-top: 1.5rem;
    padding: 1.2rem;
  }
  @media (max-width: 480px) {
    padding: 1rem;
    margin-top: 1.2rem;
  }
  h3 {
    color: #fff;
    text-shadow: 0 0 8px #2d6cffcc;
    @media (max-width: 768px) {
      font-size: 1.1rem;
      margin-bottom: 0.8rem;
    }
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid #2d6cff44;
  color: #b6d0ff;
  &:last-child {
    border-bottom: none;
    font-weight: bold;
  }
  @media (max-width: 480px) {
    padding: 0.6rem 0;
    font-size: 0.95rem;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
  gap: 1rem;
  
  button {
    min-width: 120px;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.8rem;
    
    button {
      width: 100%;
    }
  }
`;

const ErrorMessage = styled.div`
  background: rgba(44, 100, 255, 0.12);
  color: rgb(255, 45, 45);
  padding: 1rem;
  border-radius: 8px;
  margin: 0 auto 2rem auto;
  text-align: center;
  font-weight: 500;
  box-shadow: 0 0 8px #2d6cff44;
  max-width: 600px;
  
  @media (max-width: 768px) {
    padding: 0.8rem;
    margin-bottom: 1.5rem;
    font-size: 0.95rem;
  }
`;

const SupportLink = styled.a`
  font-weight: bold;
  color: #2d6cff;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
    color: #3a8fff;
  }
`;

// Screenshot images array
const screenshots = [
  { src: loginScreen, alt: "Simple Login Interface" },
  { src: mainScreen, alt: "Main Application Control Panel" },
  { src: onlineTest, alt: "Live Camera Feed Testing" }
];

// Camera plan - fixed $300 price
const cameraPlans = [
  {
    id: 'camera_lifetime',
    name: 'Camera Pro - Lifetime',
    price: 300, // $300
    currency: 'USD',
    features: [
                          'Upload your ID photo and apply realistic camera effects',
      'Works on all major platforms and verification sites',
      'Lifetime license - pay once, use forever',
      'HWID device tracking and management system',
      'Multi-device support for maximum flexibility',
      'Priority customer support via Telegram',
      'Regular software updates and improvements',
      'Safe and undetectable by websites',
      'Perfect for ID verification and video calls',
      'Professional-grade video quality output',
      'Easy-to-use interface with one-click setup',
      'Compatible with Windows systems'
    ],
    featured: true
  }
];

// Available cryptocurrencies
const cryptoOptions = [
  { id: 'ltc', name: 'Litecoin', icon: 'Ł' },
  { id: 'btc', name: 'Bitcoin', icon: '₿' },
  { id: 'eth', name: 'Ethereum', icon: 'Ξ' },
  { id: 'usdttrc20', name: 'USDT (TRC-20)', icon: '₮', note: 'Low fees' },
  { id: 'usdterc20', name: 'USDT (ERC-20)', icon: '₮', note: 'Higher fees' }
];

function CameraPage() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedCrypto, setSelectedCrypto] = useState('ltc');
  const [step, setStep] = useState(1); // 1: info, 2: payment
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentEstimate, setPaymentEstimate] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get the camera plan
  const currentPlan = cameraPlans[0];
  
  // Auto-advance carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        (prevIndex + 1) % screenshots.length
      );
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };
  
  // When user selects a new cryptocurrency, get an estimate
  useEffect(() => {
    if (step === 2 && selectedCrypto && currentPlan) {
      // Price is already in USD
      const usdPrice = currentPlan.price; // $300
      
      // Get real exchange rate estimate from NOWPayments
      const getPaymentEstimate = async () => {
        try {
          const response = await fetch('https://api.nowpayments.io/v1/estimate', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              amount: usdPrice,
              currency_from: 'usd',
              currency_to: selectedCrypto
            })
          });
          const data = await response.json();
          if (data.estimated_amount) {
            setPaymentEstimate({
              fiat: usdPrice,
              usd: currentPlan.price,
              crypto: data.estimated_amount.toFixed(8),
              currency: selectedCrypto
            });
          }
        } catch (error) {
          console.error('Error getting estimate:', error);
          // Fallback to showing just USD if estimate fails
          setPaymentEstimate({
            fiat: usdPrice,
            usd: currentPlan.price,
            crypto: '---',
            currency: selectedCrypto
          });
        }
      };
      getPaymentEstimate();
    }
  }, [step, selectedCrypto, currentPlan]);
  
  const proceedToPayment = () => {
    setStep(2);
  };
  
  const handleCryptoSelect = (cryptoId) => {
    setSelectedCrypto(cryptoId);
  };
  
  const handlePurchase = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Price is already in USD
      const usdPrice = currentPlan.price; // $300
      
      const response = await fetch('/api/payments/camera/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan_id: 'camera_lifetime',
          crypto_currency: selectedCrypto,
          price_usd: usdPrice
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        navigate(`/status/${data.payment.payment_id}`);
      } else {
        setError(data.message || 'Error creating payment. Please try again.');
      }
    } catch (error) {
      console.error('Payment creation error:', error);
      setError('Network error. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <PageBackground>
      <BackgroundPattern />
      <SimpleLayout>
        {step === 2 && (
          <PaymentHeader>
            <PaymentHeaderContent>
              <PaymentTitle>Complete Your Purchase - DESERVED CAM</PaymentTitle>
              <PaymentNavLinks>
                <PaymentNavLink onClick={() => setStep(1)}>
                  ← Back to Product
                </PaymentNavLink>
                <PaymentNavLink as={Link} to="/" variant="primary">
                  Gateway
                </PaymentNavLink>
              </PaymentNavLinks>
            </PaymentHeaderContent>
          </PaymentHeader>
        )}
        
        <PageContent>
          {step === 1 ? (
            <>
              <HeaderSection>
                <BackToGateway to="/">← Gateway</BackToGateway>
                <HeaderContainer>
                  <UrgencyBanner>
                    🔥 Pass any verification instantly • Trusted by 100+ users for KYC/ID checks
                  </UrgencyBanner>
                  <HeaderTitle>DESERVED CAM</HeaderTitle>
                  <HeaderDescription>
                    Stop failing ID verifications forever. Our professional virtual camera software transforms your ID photo into 
                    realistic video feed with authentic camera effects that passes Amazon, Revolut, Onfido, and other major verification systems.
                  </HeaderDescription>
                  <StatsBar>
                    <StatItem>
                      <StatNumber>100+</StatNumber>
                      <StatLabel>Verified Users</StatLabel>
                    </StatItem>
                    <StatItem>
                      <StatNumber>99%</StatNumber>
                      <StatLabel>Pass Rate</StatLabel>
                    </StatItem>
                    <StatItem>
                      <StatNumber>Lifetime</StatNumber>
                      <StatLabel>License</StatLabel>
                    </StatItem>
                  </StatsBar>
                  <PricingInfo>
                    <div style={{ marginBottom: '1.5rem' }}>
                      <div style={{ color: '#10b981', fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                        💎 LIFETIME LICENSE
                      </div>
                      <PricingTitle>$300</PricingTitle>
                      <div style={{ color: '#b6d0ff', fontSize: '0.9rem', opacity: 0.8 }}>
                        Save $1000s vs monthly verification services
                      </div>
                    </div>
                    <PricingDetails>
                      <PricingFeature>Lifetime access</PricingFeature>
                      <PricingFeature>No monthly fees</PricingFeature>
                      <PricingFeature>Priority support</PricingFeature>
                    </PricingDetails>
                  </PricingInfo>
                  <HeaderButton onClick={() => setStep(2)}>
                    GET LIFETIME ACCESS
                  </HeaderButton>
                  <ConfirmationText>
                    Confirmed working on Amazon, Onfido (Revolut and Backmarket), WorldCheck, Jumio & other KYC systems
                  </ConfirmationText>
                </HeaderContainer>
              </HeaderSection>

              <DetailedFeaturesSection>
                <DetailedFeaturesContainer>
                  <DetailedFeaturesTitle>Never Fail ID Verification Again</DetailedFeaturesTitle>
                  <DetailedFeaturesDescription>
                    DeservedCam solves the #1 problem with online verification: failed ID checks due to poor lighting, camera quality, 
                    or movement detection. Our advanced software creates perfect, realistic video streams every time.
                  </DetailedFeaturesDescription>
                  <FeatureContainer>
                    <FeatureCard>
                      <FeatureIcon>📱</FeatureIcon>
                      <FeatureTitle>Realistic Camera Effects</FeatureTitle>
                      <FeatureDescription>
                        Apply authentic phone camera effects to your images and videos for natural, realistic appearance
                      </FeatureDescription>
                    </FeatureCard>
                    <FeatureCard>
                      <FeatureIcon>🎯</FeatureIcon>
                      <FeatureTitle>Universal Compatibility</FeatureTitle>
                      <FeatureDescription>
                        Works with Amazon, Revolut, Onfido, WorldCheck, Jumio, and all major KYC systems
                      </FeatureDescription>
                    </FeatureCard>
                    <FeatureCard>
                      <FeatureIcon>🔒</FeatureIcon>
                      <FeatureTitle>Undetectable Technology</FeatureTitle>
                      <FeatureDescription>
                        Advanced algorithms ensure the camera feed with realistic effects passes all liveness detection systems
                      </FeatureDescription>
                    </FeatureCard>
                    <FeatureCard>
                      <FeatureIcon>⚡</FeatureIcon>
                      <FeatureTitle>Instant Setup</FeatureTitle>
                      <FeatureDescription>
                        Upload your ID photo, click start, and begin verifying - no complex configuration needed
                      </FeatureDescription>
                    </FeatureCard>
                    <FeatureCard>
                      <FeatureIcon>💻</FeatureIcon>
                      <FeatureTitle>Virtual Camera Driver</FeatureTitle>
                      <FeatureDescription>
                        Professional virtual webcam that works with any application requiring camera access
                      </FeatureDescription>
                    </FeatureCard>
                    <FeatureCard>
                      <FeatureIcon>🎨</FeatureIcon>
                      <FeatureTitle>HD Quality Output</FeatureTitle>
                      <FeatureDescription>
                        Crystal clear video output with professional lighting and perfect image quality
                      </FeatureDescription>
                    </FeatureCard>
                  </FeatureContainer>
                </DetailedFeaturesContainer>
              </DetailedFeaturesSection>

              <CarouselSection>
                <CarouselTitle>See DeservedCam in Action</CarouselTitle>
                <CarouselContainer>
                  {screenshots.map((screenshot, index) => (
                    <CarouselImage
                      key={index}
                      index={index}
                      src={screenshot.src}
                      alt={screenshot.alt}
                      $active={index === currentImageIndex}
                    />
                  ))}
                  <CarouselControls>
                    {screenshots.map((_, index) => (
                      <CarouselDot
                        key={index}
                        $active={index === currentImageIndex}
                        onClick={() => goToImage(index)}
                      />
                    ))}
                  </CarouselControls>
                </CarouselContainer>
              </CarouselSection>

              <TestimonialSection>
                <TestimonialTitle>What Our Users Say</TestimonialTitle>
                <TestimonialGrid>
                  <TestimonialCard>
                    <TestimonialText>
                                              "Finally passed Amazon verification after months of failed attempts. The camera effects are so realistic, it worked on the first try."
                    </TestimonialText>
                    <TestimonialAuthor>— Anonymous User</TestimonialAuthor>
                  </TestimonialCard>
                  <TestimonialCard>
                    <TestimonialText>
                      "Used it for Revolut KYC and it passed instantly. The quality is incredible - looks completely natural and professional."
                    </TestimonialText>
                    <TestimonialAuthor>— Anonymous User</TestimonialAuthor>
                  </TestimonialCard>
                  <TestimonialCard>
                    <TestimonialText>
                      "Saved me hours of frustration with failed verifications. Worth every penny for the lifetime license."
                    </TestimonialText>
                    <TestimonialAuthor>— Anonymous User</TestimonialAuthor>
                  </TestimonialCard>
                </TestimonialGrid>
              </TestimonialSection>

              <TrustSignals>
                <TrustItem>Works with any verification system</TrustItem>
                <TrustItem>Lifetime license included</TrustItem>
                <TrustItem>Instant download after payment</TrustItem>
                <TrustItem>Priority Telegram support</TrustItem>
                <TrustItem>100% undetectable technology</TrustItem>
              </TrustSignals>
            </>
          ) : (
            <PaymentContentWrapper>
              {error && (
                <ErrorMessage>{error}</ErrorMessage>
              )}
              
              <Container narrow>
                <HeaderTitle style={{ marginBottom: '1rem', textAlign: 'center' }}>
                  You're Almost There! 🚀
                </HeaderTitle>
                
                <div style={{ 
                  maxWidth: '900px', 
                  margin: '0 auto 3rem auto', 
                  padding: '2.5rem 2rem', 
                  background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%), linear-gradient(145deg, rgba(30, 42, 74, 0.8) 0%, rgba(20, 30, 60, 0.9) 100%)', 
                  borderRadius: '24px', 
                  border: '1px solid rgba(110, 168, 255, 0.2)', 
                  backdropFilter: 'blur(20px)', 
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)', 
                  textAlign: 'center', 
                  color: '#fff' 
                }}>
                  <p style={{ 
                    color: '#b6d0ff', 
                    fontSize: '1.15rem', 
                    margin: '0 auto', 
                    maxWidth: '700px', 
                    lineHeight: '1.7', 
                    opacity: '0.9' 
                  }}>
                    You've chosen DeservedCam Lifetime License. Complete your purchase below to get instant access to the world's most advanced virtual camera software and start passing any verification system today.
                  </p>
                </div>
                
                <CryptoOptions>
                  <CryptoOptionsTitle>Choose Your Payment Method</CryptoOptionsTitle>
                  
                  {/* Main Cryptocurrencies Row */}
                  <CryptoSelector>
                    {cryptoOptions.filter(crypto => ['ltc', 'btc', 'eth'].includes(crypto.id)).map(crypto => (
                      <CryptoOption 
                        key={crypto.id}
                        selected={selectedCrypto === crypto.id}
                        onClick={() => handleCryptoSelect(crypto.id)}
                      >
                        <CryptoIcon>{crypto.icon}</CryptoIcon>
                        <CryptoName selected={selectedCrypto === crypto.id}>
                          {crypto.name}
                        </CryptoName>
                        {crypto.note && (
                          <div style={{ fontSize: '0.7rem', color: '#b6d0ff', marginTop: '0.3rem' }}>
                            {crypto.note}
                          </div>
                        )}
                      </CryptoOption>
                    ))}
                  </CryptoSelector>

                  {/* USDT Options Row - Centered */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    gap: '1rem', 
                    marginTop: '1rem',
                    flexWrap: 'wrap'
                  }}>
                    {cryptoOptions.filter(crypto => crypto.id.includes('usdt')).map(crypto => (
                      <CryptoOption 
                        key={crypto.id}
                        selected={selectedCrypto === crypto.id}
                        onClick={() => handleCryptoSelect(crypto.id)}
                        style={{ minWidth: '120px' }}
                      >
                        <CryptoIcon>{crypto.icon}</CryptoIcon>
                        <CryptoName selected={selectedCrypto === crypto.id}>
                          {crypto.name}
                        </CryptoName>
                        {crypto.note && (
                          <div style={{ fontSize: '0.7rem', color: '#b6d0ff', marginTop: '0.3rem' }}>
                            {crypto.note}
                          </div>
                        )}
                      </CryptoOption>
                    ))}
                  </div>
                  
                  <p style={{ textAlign: 'center', fontSize: '0.9rem', marginTop: '1rem', color: '#b6d0ff' }}>
                    Need different payment options? We also accept BNB, XRP, UK Bank Transfer, or PayPal. <br />
                    <SupportLink href="https://t.me/deserved0" target="_blank" rel="noopener noreferrer">
                      Contact @deserved0 on Telegram
                    </SupportLink> for alternative methods.
                  </p>
                </CryptoOptions>
                
                {paymentEstimate && (
                  <PaymentSummary>
                    <h3>Order Summary</h3>
                    <SummaryRow>
                      <span>Product:</span>
                      <span>{currentPlan.name}</span>
                    </SummaryRow>
                    <SummaryRow>
                      <span>Price (USD):</span>
                      <span>${paymentEstimate.usd}</span>
                    </SummaryRow>
                    <SummaryRow>
                      <span>Payment Method:</span>
                      <span>{cryptoOptions.find(c => c.id === selectedCrypto).name}</span>
                    </SummaryRow>
                    <SummaryRow style={{ fontWeight: 'bold', color: '#10b981', fontSize: '1.1rem' }}>
                      <span>Access:</span>
                      <span>Instant after payment ✓</span>
                    </SummaryRow>
                  </PaymentSummary>
                )}
                
                <div style={{ 
                  textAlign: 'center', 
                  margin: '2rem auto', 
                  padding: '1rem', 
                  background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)', 
                  borderRadius: '12px', 
                  border: '1px solid rgba(110, 168, 255, 0.1)' 
                }}>
                  <p style={{ color: '#b6d0ff', fontSize: '0.9rem', margin: 0 }}>
                    🔒 Secure crypto payments • ⚡ Instant access • 💬 Telegram support included
                  </p>
                </div>
                
                <ButtonContainer>
                  <Button 
                    secondary
                    onClick={() => setStep(1)}
                    disabled={loading}
                  >
                    ← Back to Product
                  </Button>
                  <Button 
                    primary
                    onClick={handlePurchase}
                    disabled={loading}
                    style={{ 
                      background: loading ? undefined : 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
                      fontSize: '1.1rem', 
                      padding: '1rem 2rem' 
                    }}
                  >
                    {loading ? 'Creating Payment...' : `Pay $${currentPlan.price} →`}
                  </Button>
                </ButtonContainer>
              </Container>
            </PaymentContentWrapper>
          )}
        </PageContent>
        
        <Footer />
      </SimpleLayout>
    </PageBackground>
  );
}

export default CameraPage; 